import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import { Layout } from "../UI/Layout/Layout.tsx";
import { Card } from "../UI/Card.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { BreadCrumbsWrapper } from "../UI/Layout/BreadCrumbsWrapper.tsx";
import { BreadCrumbsItem } from "../UI/Layout/BreadCrumbsItem.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { OutlinedButton } from "../UI/OutlinedButton.tsx";
import { TextButton } from "../UI/TextButton.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { useToast } from "../UI/Feedback/useToast.ts";
import {
  OnboardingLiveItemFragment,
  OnboardingLiveSlotPlanItemFragment,
  OnboardingLiveSlotPlanStatus,
} from "../gql/graphql.ts";
import { useOnboardingLive } from "./useOnboardingLive.ts";
import { useOnboardingLiveConductors } from "./useOnboardingLiveConductors.ts";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";
import { ConductorSelect } from "./ConductorSelect.tsx";
import { PlanIssueList } from "./PlanIssueList.tsx";
import { PlanStatusBadge } from "./PlanStatusBadge.tsx";
import { formatSlot, onboardingLiveTimezone } from "./onboardingLiveTime.ts";
import {
  OnboardingLivesScreenPath,
  onboardingLivePlanPathOf,
} from "./OnboardingLivePaths.ts";

function toErrorMessage(e: unknown): string {
  return (e as ApolloError)?.message ?? "Errore";
}

function InfoCard({
  onboardingLive,
}: {
  onboardingLive: OnboardingLiveItemFragment;
}) {
  const navigate = useNavigate();
  const { teacherNameOf } = useOnboardingLiveConductors();
  const { userGroups } = useAllUserGroups();
  const userGroupName =
    userGroups.find((g) => g.id === onboardingLive.userGroupId)?.name ??
    onboardingLive.userGroupId;
  const end = dayjs(onboardingLive.endUtc)
    .tz(onboardingLiveTimezone)
    .format("HH:mm");
  return (
    <div className={"grid grid-cols-1 md:grid-cols-3 gap-3 text-sm"}>
      <div>
        <p className={"text-slate-500"}>Quando</p>
        <p className={"font-semibold"}>
          {formatSlot(onboardingLive.startUtc)} - {end}
        </p>
      </div>
      <div>
        <p className={"text-slate-500"}>User group</p>
        <p className={"font-semibold"}>{userGroupName}</p>
      </div>
      <div>
        <p className={"text-slate-500"}>Conduttore</p>
        <p className={"font-semibold"}>
          {teacherNameOf(onboardingLive.teacherId)}
        </p>
      </div>
      <div>
        <p className={"text-slate-500"}>Iscritti</p>
        <p className={"font-semibold"}>
          {onboardingLive.participantsCount} / {onboardingLive.capacity}
        </p>
      </div>
      <div>
        <p className={"text-slate-500"}>Link</p>
        <p className={"space-x-3"}>
          <a
            className={"underline"}
            href={onboardingLive.meetingUri}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            Google Meet
          </a>
          {onboardingLive.pdfUrl ? (
            <a
              className={"underline"}
              href={onboardingLive.pdfUrl}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              PDF
            </a>
          ) : null}
        </p>
      </div>
      {onboardingLive.creationIntentId ? (
        <div>
          <p className={"text-slate-500"}>Pianificazione</p>
          <TextButton
            label={"Apri pianificazione"}
            onClick={() =>
              navigate(
                onboardingLivePlanPathOf(onboardingLive.creationIntentId ?? ""),
              )
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function FeedbackCard({
  onboardingLive,
}: {
  onboardingLive: OnboardingLiveItemFragment;
}) {
  const feedback = onboardingLive.feedback;
  return (
    <Card>
      <h2 className={"font-bold text-lg mb-3"}>Feedback del conduttore</h2>
      {!onboardingLive.hasStarted ? (
        <p className={"text-sm text-slate-500"}>
          Il conduttore potrà lasciare il feedback dopo l'inizio della live.
        </p>
      ) : !feedback ? (
        <p className={"text-sm text-amber-700"}>Feedback non ancora inviato.</p>
      ) : (
        <div className={"space-y-2 text-sm"}>
          <p>
            Partecipanti effettivi:{" "}
            <span className={"font-semibold"}>
              {feedback.participantsCount}
            </span>
          </p>
          <div>
            <p className={"text-slate-500"}>Note</p>
            <p className={"whitespace-pre-wrap"}>{feedback.notes ?? "-"}</p>
          </div>
          <div>
            <p className={"text-slate-500"}>Criticità</p>
            <p className={"whitespace-pre-wrap"}>{feedback.issues ?? "-"}</p>
          </div>
          <p className={"text-xs text-slate-500"}>
            Inviato il {dayjs(feedback.createdAtUtc).format("DD/MM/YYYY HH:mm")}
            {feedback.updatedAtUtc
              ? ` · aggiornato il ${dayjs(feedback.updatedAtUtc).format("DD/MM/YYYY HH:mm")}`
              : ""}
          </p>
        </div>
      )}
    </Card>
  );
}

function ParticipantsCard({
  onboardingLive,
  addParticipants,
  removeParticipant,
  updating,
}: {
  onboardingLive: OnboardingLiveItemFragment;
  addParticipants: (emails: string[]) => Promise<void>;
  removeParticipant: (userId: string) => Promise<void>;
  updating: boolean;
}) {
  const [emails, setEmails] = useState("");
  const askConfirmation = useConfirm();
  const showToast = useToast();
  const canChangeParticipants = !onboardingLive.hasStarted;

  async function add() {
    const userEmails = emails
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (userEmails.length <= 0) return;
    try {
      await addParticipants(userEmails);
      setEmails("");
      showToast("Partecipanti aggiunti");
    } catch (e) {
      showToast(toErrorMessage(e), "error");
    }
  }

  async function remove(userId: string, name: string) {
    const confirmed = await askConfirmation({
      title: `Rimuovere ${name} dalla live?`,
      message:
        "Lo studente riceverà una mail di cancellazione e dovrà prenotare un'altra live.",
      confirmLabel: "Rimuovi",
      isDestructive: true,
    });
    if (!confirmed) return;
    try {
      await removeParticipant(userId);
    } catch (e) {
      showToast(toErrorMessage(e), "error");
    }
  }

  return (
    <Card>
      <h2 className={"font-bold text-lg mb-3"}>
        Partecipanti ({onboardingLive.participants.length})
      </h2>
      {canChangeParticipants ? (
        <div className={"flex flex-col md:flex-row gap-2 mb-4 md:items-end"}>
          <InputWithLabel
            label={"Aggiungi studenti per email (separate da virgola)"}
          >
            <TextInput
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </InputWithLabel>
          <OutlinedButton label={"Aggiungi"} loading={updating} onClick={add} />
        </div>
      ) : null}
      {onboardingLive.participants.length <= 0 ? (
        <p className={"text-sm text-slate-500"}>Nessuno studente iscritto.</p>
      ) : (
        <ul className={"divide-y max-h-96 overflow-y-auto"}>
          {onboardingLive.participants.map((p) => {
            const name = `${p.givenName} ${p.familyName}`;
            return (
              <li
                key={p.id}
                className={"py-2 flex items-center justify-between text-sm"}
              >
                <span>
                  {name}{" "}
                  <span className={"text-slate-500"}>· {p.speakingLevel}</span>
                </span>
                {canChangeParticipants ? (
                  <TextButton
                    label={"Rimuovi"}
                    onClick={() => remove(p.id, name)}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function ConductorChangeCard({
  onboardingLive,
  checkConductorChange,
  changeConductor,
  checking,
  changing,
}: {
  onboardingLive: OnboardingLiveItemFragment;
  checkConductorChange: (
    teacherId?: string,
  ) => Promise<OnboardingLiveSlotPlanItemFragment | undefined>;
  changeConductor: (input: {
    teacherId?: string;
    makeReplacedConductorAvailableAgain: boolean;
  }) => Promise<{
    plan?: OnboardingLiveSlotPlanItemFragment;
    changed: boolean;
  }>;
  checking: boolean;
  changing: boolean;
}) {
  const [teacherId, setTeacherId] = useState<string>();
  const [plan, setPlan] = useState<OnboardingLiveSlotPlanItemFragment>();
  const [makeAvailableAgain, setMakeAvailableAgain] = useState(true);
  const { teacherNameOf } = useOnboardingLiveConductors();
  const askConfirmation = useConfirm();
  const showToast = useToast();

  async function verify() {
    try {
      setPlan(await checkConductorChange(teacherId));
    } catch (e) {
      showToast(toErrorMessage(e), "error");
    }
  }

  async function confirmChange() {
    if (!plan?.teacherId) return;
    const confirmed = await askConfirmation({
      title: `Sostituire il conduttore con ${teacherNameOf(plan.teacherId)}?`,
      message: makeAvailableAgain
        ? "Il conduttore sostituito tornerà disponibile per altre classi in questo slot."
        : "Il conduttore sostituito NON tornerà disponibile per altre classi in questo slot.",
      confirmLabel: "Sostituisci",
    });
    if (!confirmed) return;
    try {
      const result = await changeConductor({
        teacherId,
        makeReplacedConductorAvailableAgain: makeAvailableAgain,
      });
      setPlan(result.plan);
      if (result.changed) {
        showToast("Conduttore sostituito");
        setPlan(undefined);
        setTeacherId(undefined);
      } else
        showToast(
          "La sostituzione non è più possibile, controlla i conflitti",
          "error",
        );
    } catch (e) {
      showToast(toErrorMessage(e), "error");
    }
  }

  if (onboardingLive.hasStarted) return null;
  return (
    <Card>
      <h2 className={"font-bold text-lg mb-3"}>Sostituisci il conduttore</h2>
      <div className={"grid grid-cols-1 md:grid-cols-3 gap-3 items-end"}>
        <InputWithLabel label={"Nuovo conduttore"}>
          <ConductorSelect
            value={teacherId}
            excludedTeacherIds={[onboardingLive.teacherId]}
            onChange={(v) => {
              setTeacherId(v);
              setPlan(undefined);
            }}
          />
        </InputWithLabel>
        <label className={"flex items-center gap-2 text-sm"}>
          <input
            type={"checkbox"}
            checked={makeAvailableAgain}
            onChange={(e) => setMakeAvailableAgain(e.target.checked)}
          />
          Il conduttore sostituito torna disponibile in questo slot
        </label>
        <OutlinedButton
          label={"Verifica"}
          loading={checking}
          onClick={verify}
        />
      </div>
      {plan ? (
        <div className={"mt-4 space-y-2"}>
          <div className={"flex items-center gap-2"}>
            <PlanStatusBadge status={plan.status} />
            {plan.teacherId ? (
              <span className={"text-sm"}>
                Nuovo conduttore:{" "}
                <span className={"font-semibold"}>
                  {teacherNameOf(plan.teacherId)}
                </span>
                {plan.forcedRoster ? " (turno forzato)" : ""}
              </span>
            ) : null}
          </div>
          <PlanIssueList issues={plan.issues} />
          {plan.status !== OnboardingLiveSlotPlanStatus.Blocked ? (
            <div className={"flex justify-end"}>
              <PrimaryButton
                label={"Conferma sostituzione"}
                loading={changing}
                onClick={confirmChange}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function DeleteCard({
  onboardingLive,
  deleteOnboardingLive,
  deleting,
}: {
  onboardingLive: OnboardingLiveItemFragment;
  deleteOnboardingLive: (makeConductorAvailableAgain: boolean) => Promise<void>;
  deleting: boolean;
}) {
  const [makeAvailableAgain, setMakeAvailableAgain] = useState(true);
  const askConfirmation = useConfirm();
  const showToast = useToast();
  const navigate = useNavigate();

  async function remove() {
    const confirmed = await askConfirmation({
      title: "Eliminare questa live onboarding?",
      message: `Il conduttore e i ${onboardingLive.participantsCount} studenti iscritti riceveranno una mail di annullamento. Gli studenti dovranno prenotare un'altra live.`,
      confirmLabel: "Elimina live",
      isDestructive: true,
    });
    if (!confirmed) return;
    try {
      await deleteOnboardingLive(makeAvailableAgain);
      showToast("Live eliminata");
      navigate(OnboardingLivesScreenPath);
    } catch (e) {
      showToast(toErrorMessage(e), "error");
    }
  }

  if (onboardingLive.hasStarted) return null;
  return (
    <Card>
      <h2 className={"font-bold text-lg mb-3"}>Elimina la live</h2>
      <div
        className={
          "flex flex-col md:flex-row md:items-center md:justify-between gap-3"
        }
      >
        <label className={"flex items-center gap-2 text-sm"}>
          <input
            type={"checkbox"}
            checked={makeAvailableAgain}
            onChange={(e) => setMakeAvailableAgain(e.target.checked)}
          />
          Il conduttore torna disponibile in questo slot
        </label>
        <OutlinedButton
          label={"Elimina live"}
          loading={deleting}
          onClick={remove}
        />
      </div>
    </Card>
  );
}

export function OnboardingLiveDetailScreen() {
  const { onboardingLiveId } = useParams<{ onboardingLiveId: string }>();
  if (!onboardingLiveId) throw new Error("onboardingLiveId is required");
  const navigate = useNavigate();
  const {
    onboardingLive,
    loading,
    checkConductorChange,
    changeConductor,
    deleteOnboardingLive,
    addParticipants,
    removeParticipant,
    checking,
    changing,
    deleting,
    updatingParticipants,
  } = useOnboardingLive(onboardingLiveId);

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          title={"Live onboarding"}
          breadCrumbs={
            <BreadCrumbsWrapper>
              <BreadCrumbsItem
                onClick={() => navigate(OnboardingLivesScreenPath)}
                title={"Live onboarding"}
              />
              <BreadCrumbsItem title={"Dettaglio"} />
            </BreadCrumbsWrapper>
          }
        />
        <div className={"mt-4"}>
          {loading && !onboardingLive ? (
            <LoadingRows />
          ) : onboardingLive ? (
            <InfoCard onboardingLive={onboardingLive} />
          ) : (
            <p className={"text-sm text-slate-500"}>
              Live non trovata o eliminata
            </p>
          )}
        </div>
      </Card>
      {onboardingLive ? (
        <div className={"mt-4 space-y-4"}>
          <FeedbackCard onboardingLive={onboardingLive} />
          <ParticipantsCard
            onboardingLive={onboardingLive}
            addParticipants={addParticipants}
            removeParticipant={removeParticipant}
            updating={updatingParticipants}
          />
          <ConductorChangeCard
            onboardingLive={onboardingLive}
            checkConductorChange={checkConductorChange}
            changeConductor={changeConductor}
            checking={checking}
            changing={changing}
          />
          <DeleteCard
            onboardingLive={onboardingLive}
            deleteOnboardingLive={deleteOnboardingLive}
            deleting={deleting}
          />
        </div>
      ) : null}
    </Layout>
  );
}
